import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "@/user/UserAuthContext";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/useReveal";
import { ActionIcon } from "@/user/workspace/wsui";
import { ACTION_LABEL, PRIMARY_ACTIONS, DANGER_ACTIONS, STATE_STYLE, PRIORITY_STYLE, prettyState, CARD_ICON } from "@/user/ceo/cards.config";
import { ceoApi } from "@/user/ceo/ceoApi";
import { MoneyMeterDelta, bandColor } from "@/user/ceo/MoneyMeter";
import { SCAN_STAGES } from "@/user/ceo/BusinessScan";

const SUGGESTIONS = [
    "Summarize today's business",
    "Can I hire 3 senior engineers this quarter?",
    "Which department is underperforming?",
    "What is my runway?",
    "Show legal risks this month",
    "Give me 3 growth ideas",
];

export default function CeoChat() {
    const { user, company, status, logout } = useUserAuth();
    const nav = useNavigate();
    const [convs, setConvs] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanStage, setScanStage] = useState(0);
    const [scanName, setScanName] = useState("");
    const [collapsed, setCollapsed] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const scroller = useRef(null);

    const refreshList = useCallback(() => ceoApi.chat.list().then(setConvs).catch(() => {}), []);

    useEffect(() => {
        if (status !== "ready") return;
        ceoApi.chat.list().then((list) => {
            setConvs(list);
            if (list.length) openConv(list[0].id);
        }).catch(() => {});
    }, [status]);

    useEffect(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    }, [messages, sending, scanning]);

    useEffect(() => {
        if (!scanning) return;
        setScanStage(0);
        const iv = setInterval(() => setScanStage((s) => Math.min(s + 1, SCAN_STAGES.length - 1)), 1500);
        return () => clearInterval(iv);
    }, [scanning]);

    async function scanFile(file) {
        if (!file || scanning || sending) return;
        let cid = activeId;
        if (!cid) {
            const c = await ceoApi.chat.create();
            setConvs((p) => [c, ...p]);
            cid = c.id;
            setActiveId(c.id);
        }
        setScanName(file.name);
        setScanning(true);
        try {
            await ceoApi.scan.upload(file, cid);
            await openConv(cid);
            refreshList();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Scan failed. Try a clearer image or a PDF.");
        } finally {
            setScanning(false);
            setScanName("");
        }
    }

    async function openConv(id) {
        setActiveId(id);
        try {
            const c = await ceoApi.chat.get(id);
            setMessages(c.messages || []);
        } catch { setMessages([]); }
    }

    async function newChat() {
        const c = await ceoApi.chat.create();
        setConvs((p) => [c, ...p]);
        setActiveId(c.id);
        setMessages([]);
    }

    async function delConv(id) {
        try {
            await ceoApi.chat.del(id);
            setConvs((prev) => {
                const next = prev.filter((c) => c.id !== id);
                if (activeId === id) {
                    if (next.length) openConv(next[0].id);
                    else { setActiveId(null); setMessages([]); }
                }
                return next;
            });
            toast.success("Thread deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete thread");
        }
    }

    async function send(text, attachments = []) {
        const body = text.trim();
        if (!body || sending) return;
        let cid = activeId;
        if (!cid) {
            const c = await ceoApi.chat.create();
            setConvs((p) => [c, ...p]);
            cid = c.id;
            setActiveId(c.id);
        }
        setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: body, attachments }]);
        setSending(true);
        try {
            const res = await ceoApi.chat.send(cid, body, attachments);
            setMessages((m) => [...m, res.message]);
            setConvs((p) => p.map((c) => (c.id === cid ? res.conversation : c)));
            refreshList();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Message failed");
            setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
        } finally {
            setSending(false);
        }
    }

    if (status === "loading") return <div className="h-screen grid place-items-center text-neutral-400">Loading…</div>;

    const activeTitle = convs.find((c) => c.id === activeId)?.title || "New Chat";

    return (
        <div className="h-screen w-full bg-white dot-grid flex overflow-hidden" data-testid="ceo-chat">
            {/* Sidebar */}
            <aside className={`${collapsed ? "w-0 -translate-x-full" : "w-72"} transition-all duration-300 shrink-0 border-r border-black/[0.06] bg-white/70 backdrop-blur-xl flex flex-col`}>
                <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img src={LOGO_URL} alt="CEO's GPT" className="h-7 w-7 logo-blend" />
                        <span className="font-display text-[15px] tracking-tight">CEO's <span className="italic">GPT</span></span>
                    </div>
                    <button onClick={() => setCollapsed(true)} className="h-7 w-7 rounded-lg hover:bg-black/5 grid place-items-center" data-testid="chat-collapse">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                </div>
                <div className="px-3">
                    <button onClick={newChat} data-testid="chat-new" className="w-full flex items-center justify-center gap-2 bg-black text-white text-[13.5px] font-medium py-2.5 rounded-xl hover:bg-neutral-800 transition-colors">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                        New Chat
                    </button>
                </div>
                <div className="mt-4 px-3 text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-400">Recent</div>
                <div className="flex-1 overflow-y-auto px-2 mt-1" data-testid="chat-conv-list">
                    {convs.map((c) => (
                        <div key={c.id} className={`group/conv relative flex items-center rounded-lg transition-colors ${activeId === c.id ? "bg-violet-50" : "hover:bg-black/[0.04]"}`}>
                            <button onClick={() => openConv(c.id)} data-testid={`chat-conv-${c.id}`}
                                className={`flex-1 min-w-0 text-left pl-3 pr-8 py-2 text-[13px] truncate ${activeId === c.id ? "text-violet-900" : "text-neutral-600"}`}>
                                {c.pinned && "📌 "}{c.title}
                            </button>
                            <button onClick={() => delConv(c.id)} data-testid={`chat-conv-delete-${c.id}`} title="Delete thread"
                                className="absolute right-1.5 h-6 w-6 rounded-md grid place-items-center text-neutral-400 opacity-0 group-hover/conv:opacity-100 hover:text-rose-600 hover:bg-rose-50 transition-all">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 12a1 1 0 001 1h8a1 1 0 001-1l1-12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    ))}
                    {convs.length === 0 && <div className="px-3 py-2 text-[12px] text-neutral-400">No conversations yet.</div>}
                </div>
                <div className="p-3 border-t border-black/[0.06] space-y-1">
                    {[["Business Scan", "/app/business-scan"], ["Decision Cards", "/app/ceo"], ["Open Dashboard", "/app/ceo"], ["Settings", "/app/settings"]].map(([label, to], i) => (
                        <button key={i} onClick={() => nav(to)} className="w-full text-left px-3 py-2 rounded-lg text-[13px] text-neutral-600 hover:bg-black/[0.04] flex items-center gap-2" data-testid={`chat-nav-${i}`}>
                            <ActionIcon name={["file", "flag", "chart", "refresh"][i]} />{label}
                        </button>
                    ))}
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-14 shrink-0 border-b border-black/[0.06] bg-white/70 backdrop-blur-xl flex items-center justify-between px-4">
                    <div className="flex items-center gap-3 min-w-0">
                        {collapsed && (
                            <button onClick={() => setCollapsed(false)} className="h-8 w-8 rounded-lg hover:bg-black/5 grid place-items-center" data-testid="chat-expand">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
                            </button>
                        )}
                        <div className="min-w-0">
                            <div className="text-[13.5px] font-medium text-black truncate">{activeTitle}</div>
                            <div className="text-[11px] text-neutral-400 truncate">{company?.name}</div>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-emerald-600 ml-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => nav("/app/ceo")} data-testid="chat-open-dashboard" className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-full bg-white border border-black/10 hover:bg-neutral-50 transition-colors">
                            <ActionIcon name="chart" /> Open Dashboard
                        </button>
                        <div className="relative">
                            <button onClick={() => setProfileOpen((v) => !v)} data-testid="chat-profile" className="h-9 w-9 rounded-full bg-violet-600 text-white grid place-items-center text-[13px] font-semibold">
                                {(user?.name || "?").slice(0, 1).toUpperCase()}
                            </button>
                            {profileOpen && (
                                <div className="absolute right-0 mt-2 w-52 glass-strong rounded-2xl p-2 z-50">
                                    <div className="px-3 py-2">
                                        <div className="text-[13px] font-medium text-black truncate">{user?.name}</div>
                                        <div className="text-[11px] text-neutral-400 truncate">{user?.email}</div>
                                    </div>
                                    <button onClick={() => { logout(); window.location.assign("/login"); }} data-testid="chat-logout" className="w-full text-left px-3 py-2 rounded-lg text-[13px] text-rose-600 hover:bg-rose-50">Logout</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <div ref={scroller} className="flex-1 overflow-y-auto">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
                        {messages.length === 0 && !sending && !scanning ? (
                            <EmptyGreeting name={user?.name} onPick={(q) => send(q)} />
                        ) : (
                            <div className="space-y-6">
                                {messages.map((m) => <MessageBubble key={m.id} m={m} onAction={handleCardAction} onFollow={(q) => send(q)} />)}
                                {sending && <TypingIndicator />}
                                {scanning && <ScanningBubble stage={scanStage} name={scanName} />}
                            </div>
                        )}
                    </div>
                </div>

                <ChatInput onSend={send} sending={sending} onNewChat={newChat} onScan={scanFile} scanning={scanning} />
            </div>
        </div>
    );

    async function handleCardAction(card, action) {
        try {
            const updated = await ceoApi.cardAction(card.id, action);
            toast.success(`${ACTION_LABEL[action] || action} recorded`);
            setMessages((ms) => ms.map((m) => {
                if (m.role !== "assistant" || !m.structured_response) return m;
                const cards = (m.structured_response.decision_cards || []).map((c) => (c.id === card.id ? updated : c));
                return { ...m, structured_response: { ...m.structured_response, decision_cards: cards } };
            }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    }
}

function EmptyGreeting({ name, onPick }) {
    const h = new Date().getHours();
    const g = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
    return (
        <div className="pt-10 text-center" data-testid="chat-empty">
            <img src={LOGO_URL} alt="" className="h-14 w-14 logo-blend mx-auto mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-black">{g}, <span className="italic">{(name || "").split(" ")[0]}</span></h1>
            <p className="mt-3 text-[15px] text-neutral-500">Ask anything about your business. I already know your company.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl mx-auto">
                {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => onPick(s)} data-testid={`chat-suggestion-${i}`}
                        className="text-left glass rounded-2xl px-4 py-3 text-[13.5px] text-neutral-700 hover:-translate-y-0.5 hover:text-black transition-all duration-300">
                        {s}
                    </button>
                ))}
            </div>
        </div>
    );
}

function IntelligenceStrip({ sr }) {
    const hasConf = typeof sr.confidence_score === "number";
    const money = sr.money_impact && typeof sr.money_impact === "object" ? sr.money_impact : {};
    const moneyRows = Object.entries(money).filter(
        ([k, v]) => k !== "is_estimate" && v !== "" && v != null
    );
    const signals = Array.isArray(sr.department_signals) ? sr.department_signals : [];
    const risks = Array.isArray(sr.risks) ? sr.risks : [];
    const missing = Array.isArray(sr.missing_data) ? sr.missing_data : [];
    if (!hasConf && !signals.length && !risks.length && !moneyRows.length && !missing.length) return null;

    const c = sr.confidence_score;
    const confColor = c >= 70 ? "bg-emerald-500" : c >= 50 ? "bg-amber-500" : "bg-rose-500";

    const sigStatus = (s) =>
        /risk|weak|low|watch|delay|churn|critical|down/i.test(s || "") ? "text-rose-600 bg-rose-50"
            : /strong|healthy|high|good|up|opportunity/i.test(s || "") ? "text-emerald-700 bg-emerald-50"
                : "text-neutral-600 bg-neutral-100";

    return (
        <div className="mt-2.5 space-y-2" data-testid="chat-intelligence">
            <div className="flex flex-wrap items-center gap-2">
                {hasConf && (
                    <span data-testid="chat-confidence" className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white border border-black/[0.07] rounded-full px-2.5 py-1">
                        <span className={`h-1.5 w-1.5 rounded-full ${confColor}`} />
                        Confidence {c}/100 · {sr.confidence_label}
                    </span>
                )}
                {missing.length > 0 && (
                    <span data-testid="chat-missing-data" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2.5 py-1">
                        Missing: {missing.slice(0, 3).join(", ")}{missing.length > 3 ? ` +${missing.length - 3}` : ""}
                    </span>
                )}
                {typeof sr.money_meter?.current === "number" && (
                    <span data-testid="chat-money-meter" className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white border border-black/[0.07] rounded-full px-2.5 py-1">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: bandColor(sr.money_meter.current) }} />
                        Money Meter {sr.money_meter.current} · {sr.money_meter.label}
                    </span>
                )}
            </div>
            {signals.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="chat-dept-signals">
                    {signals.slice(0, 8).map((s, i) => {
                        const dep = typeof s === "object" ? s.department : "";
                        const sig = typeof s === "object" ? (s.signal || s.status || "") : s;
                        return (
                            <span key={i} className={`text-[11px] rounded-full px-2.5 py-1 ${sigStatus(typeof s === "object" ? `${s.signal} ${s.status}` : s)}`}>
                                {dep ? <b className="font-semibold">{dep}:</b> : null} {sig}
                            </span>
                        );
                    })}
                </div>
            )}
            {risks.length > 0 && (
                <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-3" data-testid="chat-risks">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-rose-500 mb-1">Risks</div>
                    <ul className="space-y-0.5">{risks.slice(0, 5).map((r, i) => (
                        <li key={i} className="text-[12.5px] text-rose-800 flex gap-2"><span>•</span><span>{typeof r === "object" ? (r.risk || r.title || JSON.stringify(r)) : r}</span></li>
                    ))}</ul>
                </div>
            )}
            {moneyRows.length > 0 && (
                <div className="rounded-2xl border border-black/[0.06] bg-neutral-50/60 p-3" data-testid="chat-money-impact">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-1.5">
                        Money Impact {money.is_estimate ? <span className="text-neutral-400 normal-case tracking-normal">(estimate)</span> : null}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {moneyRows.map(([k, v]) => (
                            <div key={k}>
                                <div className="text-[10px] text-neutral-400 capitalize">{k.replace(/_/g, " ")}</div>
                                <div className="text-[13px] font-medium text-neutral-800">{String(v)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function MessageBubble({ m, onAction, onFollow }) {
    if (m.role === "user") {
        return (
            <div className="flex justify-end" data-testid="chat-msg-user">
                <div className="max-w-[85%] bg-black text-white rounded-3xl rounded-br-lg px-5 py-3 text-[14.5px] leading-relaxed">
                    {m.content}
                    {m.attachments?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {m.attachments.map((a, i) => <span key={i} className="text-[11px] bg-white/15 rounded-full px-2 py-0.5">📎 {a.filename}</span>)}
                        </div>
                    )}
                </div>
            </div>
        );
    }
    const sr = m.structured_response || {};
    return (
        <div className="flex gap-3" data-testid="chat-msg-ai">
            <img src={LOGO_URL} alt="" className="h-8 w-8 logo-blend shrink-0 mt-1" />
            <div className="min-w-0 flex-1">
                <div className="glass rounded-3xl rounded-tl-lg px-5 py-4">
                    <Markdown text={m.content} />
                </div>
                <IntelligenceStrip sr={sr} />
                {sr.recommendations?.length > 0 && (
                    <div className="mt-3 rounded-2xl border border-black/[0.06] bg-neutral-50/60 p-4">
                        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-1.5">Recommendations</div>
                        <ul className="space-y-1">{sr.recommendations.map((r, i) => <li key={i} className="text-[13px] text-neutral-700 flex gap-2"><span className="text-violet-500">•</span>{r}</li>)}</ul>
                    </div>
                )}
                {sr.decision_cards?.length > 0 && (
                    <div className="mt-3 space-y-3">
                        {sr.decision_cards.map((c) => <InlineCard key={c.id} card={c} onAction={onAction} />)}
                    </div>
                )}
                {sr.citations?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {sr.citations.map((c, i) => <span key={i} className="text-[11px] text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5">{c}</span>)}
                    </div>
                )}
                {sr.next_questions?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="chat-followups">
                        {sr.next_questions.map((q, i) => (
                            <button key={i} onClick={() => onFollow(q)} data-testid={`chat-followup-${i}`}
                                className="text-[12.5px] text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-full px-3 py-1.5 transition-colors">
                                {q}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function InlineCard({ card, onAction }) {
    const quick = (card.actions || []).filter((a) => a !== "view_details").slice(0, 3);
    return (
        <div data-testid={`chat-card-${card.id}`} className="relative glass rounded-2xl p-5 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(120% 90% at 100% 0%, rgba(139,92,246,0.08), transparent 55%)" }} />
            <div className="relative">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center"><ActionIcon name={CARD_ICON[card.card_type] || "flag"} /></span>
                        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-600">Decision Card</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Pill cls={PRIORITY_STYLE[card.priority] || PRIORITY_STYLE.medium}>{card.priority}</Pill>
                        <Pill cls={STATE_STYLE[card.state] || STATE_STYLE.generated}>{prettyState(card.state)}</Pill>
                    </div>
                </div>
                <div className="mt-3 font-display text-xl font-medium text-black">{card.title}</div>
                {card.summary && <p className="mt-1.5 text-[13px] text-neutral-600 leading-relaxed">{card.summary}</p>}
                {card.metrics?.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {card.metrics.slice(0, 3).map((mt, i) => (
                            <div key={i} className="rounded-xl bg-neutral-50/70 border border-black/5 p-2.5">
                                <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-neutral-400 truncate">{mt.label}</div>
                                <div className="text-[15px] font-medium text-black truncate">{mt.value}</div>
                            </div>
                        ))}
                    </div>
                )}
                {card.money_meter && (card.money_meter.before != null || card.money_meter.after != null) && (
                    <MoneyMeterDelta before={card.money_meter.before} after={card.money_meter.after} reason={card.money_meter.reason} />
                )}
                {card.recommended_action && (
                    <div className="mt-3 text-[12.5px] text-neutral-600"><span className="text-violet-600 font-medium">Recommended:</span> {card.recommended_action}</div>
                )}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {quick.map((a) => (
                        <button key={a} onClick={() => onAction(card, a)} data-testid={`chat-card-action-${a}-${card.id}`}
                            className={`text-[12.5px] font-medium px-3.5 py-1.5 rounded-full transition-colors ${PRIMARY_ACTIONS.has(a) ? "bg-black text-white hover:bg-neutral-800" : DANGER_ACTIONS.has(a) ? "bg-white text-rose-600 border border-rose-200 hover:bg-rose-50" : "bg-white text-black border border-black/10 hover:bg-neutral-50"}`}>
                            {ACTION_LABEL[a] || a}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex gap-3" data-testid="chat-typing">
            <img src={LOGO_URL} alt="" className="h-8 w-8 logo-blend shrink-0 mt-1" />
            <div className="glass rounded-3xl rounded-tl-lg px-5 py-4 flex items-center gap-1.5">
                {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
            </div>
        </div>
    );
}

function ScanningBubble({ stage, name }) {
    return (
        <div className="flex gap-3" data-testid="chat-scanning">
            <img src={LOGO_URL} alt="" className="h-8 w-8 logo-blend shrink-0 mt-1" />
            <div className="glass rounded-3xl rounded-tl-lg px-5 py-4 min-w-[260px] relative overflow-hidden">
                <div className="scan-beam-h absolute inset-x-0 h-10 pointer-events-none" />
                <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-600">Business Scan</div>
                <div className="mt-1 text-[13.5px] text-neutral-700 truncate">📎 {name}</div>
                <div className="mt-3 text-[14px] font-medium text-violet-700" data-testid="chat-scan-stage">{SCAN_STAGES[stage]}</div>
                <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${((stage + 1) / SCAN_STAGES.length) * 100}%`, background: "linear-gradient(90deg,#a78bfa,#6366f1)" }} />
                </div>
                <style>{`.scan-beam-h{background:linear-gradient(180deg,transparent,rgba(139,92,246,0.35),transparent);animation:sbh 1.6s ease-in-out infinite;}@keyframes sbh{0%{top:-10%}100%{top:110%}}`}</style>
            </div>
        </div>
    );
}

function ChatInput({ onSend, sending, onNewChat, onScan, scanning }) {
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const ref = useRef(null);
    const scanRef = useRef(null);

    function submit() {
        if (!text.trim() || sending) return;
        onSend(text, files);
        setText("");
        setFiles([]);
        if (ref.current) ref.current.style.height = "auto";
    }
    function onKey(e) {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
    }
    function pickFiles(e) {
        const fs = Array.from(e.target.files || []).map((f) => ({ filename: f.name, size: f.size, content_type: f.type }));
        setFiles((p) => [...p, ...fs]);
        e.target.value = "";
    }

    return (
        <div className="shrink-0 border-t border-black/[0.06] bg-white/70 backdrop-blur-xl px-4 py-4">
            <div className="max-w-3xl mx-auto">
                {files.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                        {files.map((f, i) => (
                            <span key={i} className="text-[11px] bg-neutral-100 rounded-full pl-2.5 pr-1.5 py-1 flex items-center gap-1.5">
                                📎 {f.filename}
                                <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-rose-500">✕</button>
                            </span>
                        ))}
                    </div>
                )}
                <div className="glass-strong rounded-3xl p-2 flex items-end gap-2">
                    <label className="h-10 w-10 shrink-0 rounded-2xl hover:bg-black/5 grid place-items-center cursor-pointer text-neutral-500" data-testid="chat-attach" title="Attach file">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 12.5l-8 8a5 5 0 01-7-7l8-8a3.3 3.3 0 015 5l-8 8a1.7 1.7 0 01-2.4-2.4l7.3-7.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        <input type="file" multiple className="hidden" onChange={pickFiles} accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.webp" data-testid="chat-file-input" />
                    </label>
                    <button
                        type="button"
                        onClick={() => scanRef.current?.click()}
                        disabled={sending || scanning}
                        data-testid="chat-business-scan"
                        title="Business Scan — upload a document or photo"
                        className="h-10 shrink-0 rounded-2xl px-3 grid place-items-center text-violet-700 bg-violet-50 hover:bg-violet-100 disabled:opacity-40 transition-colors"
                    >
                        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 7V5a1 1 0 011-1h2M17 4h2a1 1 0 011 1v2M20 17v2a1 1 0 01-1 1h-2M7 20H5a1 1 0 01-1-1v-2M4 12h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            <span className="hidden sm:inline">Scan</span>
                        </span>
                        <input ref={scanRef} type="file" className="hidden" data-testid="chat-scan-input"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.csv,.txt,.xlsx,.xls,.docx"
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f && onScan) onScan(f); }} />
                    </button>
                    <textarea
                        ref={ref}
                        rows={1}
                        value={text}
                        onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px"; }}
                        onKeyDown={onKey}
                        placeholder="Ask anything about your business…"
                        data-testid="chat-input"
                        className="flex-1 resize-none bg-transparent outline-none text-[15px] text-black placeholder:text-neutral-400 py-2.5 max-h-40"
                    />
                    <button onClick={submit} disabled={!text.trim() || sending} data-testid="chat-send"
                        className="h-10 w-10 shrink-0 rounded-2xl bg-black text-white grid place-items-center disabled:opacity-30 hover:bg-neutral-800 transition-colors">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 20V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                </div>
                <div className="mt-1.5 text-center text-[11px] text-neutral-400">CEO's GPT uses your live company data · <button onClick={onNewChat} className="hover:text-black" data-testid="chat-newchat-footer">New chat</button></div>
            </div>
        </div>
    );
}

function Pill({ cls, children }) {
    return <span className={`inline-flex items-center text-[10px] font-semibold tracking-[0.1em] uppercase rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

export function CeoChatRoute() {
    const { status, user } = useUserAuth();
    if (status === "loading") return <div className="h-screen grid place-items-center text-neutral-400">Loading…</div>;
    if (status === "guest") return <RedirectTo to="/login" />;
    if (user?.must_change_password) return <RedirectTo to="/change-password" />;
    if (user?.role !== "ceo") {
        const map = { sales: "sales", finance: "finance", hr: "hr", operations: "operations", marketing: "marketing", customer: "customer-support", procurement: "procurement", legal: "legal", strategy: "strategy", meeting: "meeting-admin", company_admin: "../company-admin/dashboard" };
        const seg = map[user?.role];
        return <RedirectTo to={seg ? `/app/${seg}` : "/app"} />;
    }
    return <CeoChat />;
}

function RedirectTo({ to }) {
    if (typeof window !== "undefined") window.location.assign(to.startsWith("../") ? "/" + to.slice(3) : to);
    return null;
}


/* Minimal, safe markdown: bold, bullets, numbered lists, line breaks. */
function Markdown({ text }) {
    const lines = String(text || "").split("\n");
    const fmt = (s) => {
        const parts = s.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>));
    };
    return (
        <div className="text-[14.5px] text-neutral-800 leading-relaxed space-y-1.5">
            {lines.map((ln, i) => {
                const t = ln.trim();
                if (!t) return <div key={i} className="h-1" />;
                if (/^[-*]\s+/.test(t)) return <div key={i} className="flex gap-2"><span className="text-violet-500">•</span><span>{fmt(t.replace(/^[-*]\s+/, ""))}</span></div>;
                if (/^\d+\.\s+/.test(t)) return <div key={i} className="flex gap-2"><span className="text-violet-500 font-medium">{t.match(/^\d+/)[0]}.</span><span>{fmt(t.replace(/^\d+\.\s+/, ""))}</span></div>;
                if (/^#{1,3}\s+/.test(t)) return <div key={i} className="font-display text-[16px] font-medium text-black mt-1">{fmt(t.replace(/^#{1,3}\s+/, ""))}</div>;
                return <p key={i}>{fmt(t)}</p>;
            })}
        </div>
    );
}
